import React, { useState } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quoteMenuOpen, setQuoteMenuOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Portfolio', href: '/portfolio' },
    { name: 'Learn More', href: '/learn-more' },
    { name: 'Contact', href: '/contact' }
  ];

  const isActive = (href) => location.pathname === href;

  const isHomePage = location.pathname === '/' || location.pathname === '/home';

  return (
    <header className={isHomePage ? 'absolute top-0 left-0 right-0 z-50' : 'sticky top-0 z-50'}>
      {/* Main nav bar */}
      <div className={isHomePage ? 'bg-transparent' : 'bg-[#1A283D]'}>
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <img
                src="/images/optimized/logo_header.webp"
                alt="Situ Builds"
                width="187"
                height="112"
                className="h-28 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex md:items-center md:gap-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-sm font-medium px-4 py-2 rounded-lg transition-all duration-200 ${
                    isActive(item.href)
                      ? 'text-white bg-white/10'
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* CTA Dropdown */}
              <div className="relative group ml-4">
                <button
                  className="bg-[#C4A77D] text-white px-6 py-2.5 rounded-lg font-semibold flex items-center gap-2 hover:bg-[#B39768] transition-colors"
                  data-testid="start-project-btn"
                >
                  <span>Get a Quote</span>
                  <ChevronDown className="w-4 h-4 transition-transform duration-300 group-hover:rotate-180" />
                </button>
                
                <div className="absolute right-0 top-full pt-2 w-56 z-[100] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
                    <Link
                      to="/deck-quote"
                      className="flex items-center gap-3 px-4 py-3 hover:bg-[#F5F0EB] transition-colors"
                      data-testid="dropdown-deck-quote"
                    >
                      <div className="w-10 h-10 rounded-lg bg-[#1A283D]/10 flex items-center justify-center">
                        <svg className="w-5 h-5 text-[#1A283D]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-semibold text-[#1A283D]">Plan Your Deck</div>
                        <div className="text-xs text-gray-500">Custom outdoor spaces</div>
                      </div>
                    </Link>
                    
                    <Link
                      to="/fence-quote"
                      className="flex items-center gap-3 px-4 py-3 hover:bg-[#F5F0EB] transition-colors"
                      data-testid="dropdown-fence-quote"
                    >
                      <div className="w-10 h-10 rounded-lg bg-[#1A283D]/10 flex items-center justify-center">
                        <svg className="w-5 h-5 text-[#1A283D]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-semibold text-[#1A283D]">Plan Your Fence</div>
                        <div className="text-xs text-gray-500">Privacy & security</div>
                      </div>
                    </Link>
                    
                    <Link
                      to="/contact"
                      className="flex items-center gap-3 px-4 py-3 hover:bg-[#F5F0EB] transition-colors border-t border-gray-100"
                      data-testid="dropdown-contact"
                    >
                      <div className="w-10 h-10 rounded-lg bg-[#1A283D]/10 flex items-center justify-center">
                        <svg className="w-5 h-5 text-[#1A283D]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-semibold text-[#1A283D]">Custom Project</div>
                        <div className="text-xs text-gray-500">Pergolas, sheds & more</div>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-white p-2"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </nav>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#1A283D] border-t border-white/10">
            <div className="px-4 py-4 space-y-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                    isActive(item.href)
                      ? 'text-white bg-white/10'
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile Quote Section */}
              <div className="pt-4 border-t border-white/10">
                <p className="px-4 text-sm text-gray-400 mb-2">Get a Quote</p>
                <Link
                  to="/deck-quote"
                  className="block px-4 py-3 text-white hover:bg-white/5 rounded-lg transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Plan Your Deck
                </Link>
                <Link
                  to="/fence-quote"
                  className="block px-4 py-3 text-white hover:bg-white/5 rounded-lg transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Plan Your Fence
                </Link>
                <Link
                  to="/contact"
                  className="block px-4 py-3 text-white hover:bg-white/5 rounded-lg transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Custom Project
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
